import type { MutableRefObject } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeTextFrameHandle } from '@/components/app/shapes/shape-text-frame-registry'
import { clientPointToShapeLocal } from '@/lib/shapes/shape-text-hit'
import { applyEditToParagraphs, applyStyleToCharRange } from '@/lib/shapes/shape-text-edits'
import { caretIndexAtShapeLocalPoint, paragraphsToPlainText } from '@/lib/shapes/shape-text'
import { buildStyledHtmlFromRange } from '@/lib/shapes/shape-text-clipboard'
import {
  caretXAtOffset,
  rowBoundaryOffset,
  rowEdgeOffsetNearestX,
  verticalStepOffset,
} from '@/lib/shapes/shape-text-navigation'
import type {
  ShapeTextCharRunMap,
  ShapeTextFonts,
  ShapeTextLayoutResult,
} from '../types/shape-text-layout-state'

/** The frame state the registry handle reads, refreshed every render. */
export interface ShapeTextFrameHandleDeps {
  layout: ShapeTextLayoutResult
  shape: ShapeData
  canvasZoom: number
  textValue: string
  charRunMap: ShapeTextCharRunMap
  paragraphs: Paragraph[]
  showsOverflowBadge: boolean
  /** The caret index while this frame is the one being edited, otherwise `null`. */
  editingCaretIndex: number | null
}

/**
 * This frame's entry in the cross-frame selection registry (WMPCM-9199). Every read goes through
 * `depsRef`/`fontsRef`, so the handle stays valid across renders without re-registering.
 */
export function createShapeTextFrameHandle({
  depsRef,
  fontsRef,
  overlayRef,
  setTextValue,
  lastEmittedModelTextRef,
}: {
  depsRef: MutableRefObject<ShapeTextFrameHandleDeps>
  fontsRef: MutableRefObject<ShapeTextFonts>
  overlayRef: MutableRefObject<HTMLDivElement | null>
  setTextValue: (value: string) => void
  lastEmittedModelTextRef: MutableRefObject<string | null>
}): ShapeTextFrameHandle {
  return {
    getOverlayRect: () => overlayRef.current?.getBoundingClientRect() ?? null,
    containsClientPoint: (clientX, clientY) => {
      const rect = overlayRef.current?.getBoundingClientRect()

      if (!rect) return false

      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      )
    },
    caretIndexAtClientPoint: (clientX, clientY) => {
      const host = overlayRef.current
      const deps = depsRef.current

      if (!host) return 0

      const local = clientPointToShapeLocal(
        clientX,
        clientY,
        host.getBoundingClientRect(),
        deps.canvasZoom,
        deps.shape.rotation ?? 0,
        deps.shape.width,
        deps.shape.height
      )

      return caretIndexAtShapeLocalPoint(deps.layout.carets, local.x, local.y)
    },
    getTextLength: () => depsRef.current.textValue.length,
    isShowingOverflowBadge: () => depsRef.current.showsOverflowBadge,
    getPlainText: () => depsRef.current.textValue,
    getConsumedLength: () => depsRef.current.layout.consumedLength,
    getEditingCaretOffset: () => depsRef.current.editingCaretIndex,
    buildStyledHtmlForRange: (start, end) => {
      const deps = depsRef.current

      // Same paragraph-style carry as `execCopy` (WMPCM-10131 Issue 2) — a cross-frame copy
      // concatenates these per frame, so omitting it here would drop alignment for every
      // frame in the selection but the caret's.
      return buildStyledHtmlFromRange(
        deps.textValue,
        deps.charRunMap,
        start,
        end,
        fontsRef.current ?? undefined,
        deps.paragraphs
      )
    },
    // `replaceRange`/`applyRunStyleToRange` deliberately do NOT call `onParagraphsChange`.
    // That path schedules the debounced commit, which diffs against the *editing* frame's
    // baseline — meaningless for the other members of a cross-frame edit. The caller
    // (`ShapeManager`) writes and commits each frame itself so the whole edit shares one
    // history transaction. The local buffer is still updated here, because only this instance
    // owns it and the editing frame must not lag its own model.
    replaceRange: (start, end, text) => {
      const deps = depsRef.current

      if (end <= start && !text) return null

      const before = deps.paragraphs
      const after = applyEditToParagraphs(before, start, end, text, null)

      const afterText = paragraphsToPlainText(after)

      setTextValue(afterText)
      // `ShapeManager` commits this write, so it never passes through `emitParagraphs` —
      // record it here or the resync effect would read the commit back as an external change.
      lastEmittedModelTextRef.current = afterText

      return { before, after }
    },
    // Row geometry lives in this frame's own `layout.carets`, so these delegate straight to
    // the pure helpers rather than re-deriving rows anywhere else (WMPCM-9200).
    rowBoundaryOffset: (offset, direction) =>
      rowBoundaryOffset(depsRef.current.layout.carets, offset, direction),
    verticalStepOffset: (offset, direction) =>
      verticalStepOffset(depsRef.current.layout.carets, offset, direction),
    caretXAtOffset: (offset) => caretXAtOffset(depsRef.current.layout.carets, offset),
    rowEdgeOffsetNearestX: (x, edge) =>
      rowEdgeOffsetNearestX(depsRef.current.layout.carets, x, edge),
    applyRunStyleToRange: (start, end, update) => {
      const deps = depsRef.current

      if (end <= start) return null

      const before = deps.paragraphs

      return { before, after: applyStyleToCharRange(before, update, start, end) }
    },
  }
}
